import React from 'react'
import {
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  ButtonGroup,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
} from '@mui/material'

import { useAppLocation, downloadFile } from '../utils'
import { useAuthToken } from '../auth'
import { TimesheetReportTable, TimesheetReportTableDataRow } from './'
import { TimesheetApi, Configuration } from '../../api'
import { useQuery } from '@tanstack/react-query'

const dropdownYears = ['2024', '2025', '2026', '2027', '2028', '2029', '2030']

interface TimesheetReportCardProps {
  reportType: 'utilisation' | 'performance'
}

export function TimesheetReportCard({ reportType }: TimesheetReportCardProps) {
  const title = reportType === 'utilisation' ? 'Utilisation Report' : 'Performance Report'

  const appLocation = useAppLocation()
  const authToken = useAuthToken()

  const apiConfiguration = new Configuration({
    basePath: appLocation.NewOpusAPI,
    accessToken: authToken.idToken, // Yes this is bad auth, Opus does bad auth...
  })
  const api = new TimesheetApi(apiConfiguration)

  const [year, setYear] = React.useState('2024')

  const handleFinancialYearChange = (event: SelectChangeEvent) => {
    setYear(event.target.value as string)
  }

  const { isPending, isError, data, error } = useQuery({
    queryKey: [reportType, year],
    queryFn: async (): Promise<Array<TimesheetReportTableDataRow>> => {
      if (reportType === 'utilisation') {
        const apiResponse = await api.getTimesheetUtilisationReport({ year: Number(year) })
        return apiResponse.entries
      } else {
        const apiResponse = await api.getTimesheetPerformanceReport({ year: Number(year) })
        return apiResponse.entries
      }
    },
  })

  const downloadCSV = async () => {
    let csvData =
      'Name,Q1 Core,Q1 Real,Q1 %,Q2 Core,Q2 Real,Q2 %,Q3 Core,Q3 Real,Q3 %,Q4 Core,Q4 Real,Q4 %,Annual Core,Annual Real,Annual %\n'
    if (reportType === 'utilisation') {
      csvData = csvData.replaceAll('Real', 'Act')
    } else {
      csvData = csvData.replaceAll('Real', 'Prod')
    }

    for (const row of data ?? []) {
      csvData += `"${row.name}",${row.coreHours[0]},${row.realHours[0]},${row.percentage[0]},${row.coreHours[1]},${row.realHours[1]},${row.percentage[1]}`
      csvData += `,${row.coreHours[2]},${row.realHours[2]},${row.percentage[2]},${row.coreHours[3]},${row.realHours[3]},${row.percentage[3]}`
      csvData += `,${row.coreHours[4]},${row.realHours[4]},${row.percentage[4]}\n`
    }

    downloadFile([csvData], 'text/csv', `${reportType}-${year}.csv`)
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" component="div">
            {title}
          </Typography>

          <FormControl variant="standard">
            <Select value={year} displayEmpty onChange={handleFinancialYearChange}>
              {dropdownYears.map(yearItem => (
                <MenuItem key={yearItem} value={yearItem}>
                  {yearItem}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ButtonGroup color="secondary">
            <Button onClick={downloadCSV} disabled={isPending || isError}>
              Export CSV
            </Button>
            <Button disabled>Export PDF</Button>
          </ButtonGroup>
        </Box>

        {isPending ? (
          <Box display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert variant="filled" severity="warning">
            Failed to load timesheet data: {error.message}
          </Alert>
        ) : (
          <TimesheetReportTable realHourColumn="Act" rows={data} />
        )}
      </CardContent>
    </Card>
  )
}
